import React, { useEffect, useState, useCallback, useRef } from "react";
import { Modal, Form, Button, Divider, Input, Select, Spin } from "antd";
import { SaveOutlined, CloseOutlined, FileTextOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getUsersList } from "../../Redux/Actions/usersActions";
import "./addAndEditUserQueryProject.css";

const { Option } = Select;
const PAGE_SIZE = 20;

const UserQueryProjectEditModal = ({ visible, onClose, recordData, onSave }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const [userOptions, setUserOptions] = useState([]);
    const [userPage, setUserPage] = useState(1);
    const [userTotal, setUserTotal] = useState(0);
    const [userFilter, setUserFilter] = useState("");
    const [usersLoading, setUsersLoading] = useState(false);

    const searchTimeoutRef = useRef(null);
    const requestIdRef = useRef(0);

    /* ── Fetch a page of users. `append` controls whether results replace
       the list (new search) or get appended (infinite scroll). ── */
    const fetchUsers = useCallback(async (page, filter, append) => {
        const thisRequestId = ++requestIdRef.current;
        setUsersLoading(true);
        try {
            const res = await dispatch(
                getUsersList(
                    {
                        page,
                        limit: PAGE_SIZE,
                        is_deleted: false,
                        filter,
                        signup_from: "app",
                    },
                    false // don't trigger global page loader for dropdown fetches
                )
            );

            // Ignore stale responses if a newer search/scroll fired since
            if (thisRequestId !== requestIdRef.current) return;

            const list = Array.isArray(res?.data) ? res.data : [];
            const total = res?.total ?? 0;

            setUserOptions((prev) => (append ? [...prev, ...list] : list));
            setUserTotal(total);
            setUserPage(page);
        } finally {
            if (thisRequestId === requestIdRef.current) setUsersLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (visible) {
            setUserFilter("");
            fetchUsers(1, "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    /* ── Debounced search — resets to page 1 and replaces the list ── */
    const handleUserSearch = (value) => {
        setUserFilter(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            fetchUsers(1, value, false);
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    /* ── Infinite scroll — load next page when nearing the bottom of the
       dropdown, as long as more results exist and nothing's in flight. ── */
    const handlePopupScroll = (e) => {
        const { target } = e;
        const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
        const hasMore = userOptions.length < userTotal;

        if (nearBottom && hasMore && !usersLoading) {
            fetchUsers(userPage + 1, userFilter, true);
        }
    };

    useEffect(() => {
        if (visible && recordData) {
            form.setFieldsValue({
                user: recordData.user?._id || recordData.user,
                project_name: recordData.project_name,
                street_address: recordData.street_address,
                builder_name: recordData.builder_name,
            });

            /* ── If editing, make sure the currently-assigned user shows up
               as a selectable option even if outside the default first page. ── */
            if (recordData.user?._id && recordData.user?.name) {
                setUserOptions((prev) =>
                    prev.some((u) => u._id === recordData.user._id)
                        ? prev
                        : [recordData.user, ...prev]
                );
            }
        } else if (visible) {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, recordData, form]);

    const handleFinish = async (values) => {
        setSubmitting(true);
        try {
            await onSave(values);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={
                <div className="uqp-modal-header-content">
                    <FileTextOutlined className="uqp-modal-header-icon" style={{ color: "white" }} />
                    <span style={{ color: "white" }}>
                        {recordData?._id ? "EDIT USER QUERY PROJECT" : "ADD USER QUERY PROJECT"}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            closeIcon={
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
                    onClick={onClose}
                    style={{
                        marginTop: 1, marginRight: 47, padding: 6,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "1px solid white", color: "white",
                    }}
                    className="uqp-modal-close-btn"
                />
            }
            destroyOnClose
            width={600}
            className="uqp-modal-override"
            styles={{
                header: {
                    background: "#070C18",
                    borderRadius: "12px 12px 0 0",
                    padding: "16px 24px",
                    borderBottom: "none",
                },
                body: { padding: "20px 24px", background: "#f5f6fa" },
            }}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish} className="uqp-modal-form">

                <div className="uqp-form-section">
                    <h3 className="uqp-form-section-header">Query Details</h3>
                    <div className="uqp-form-grid">
                        <Form.Item
                            label="User"
                            name="user"
                            rules={[{ required: true, message: "Please select a user" }]}
                            className="uqp-form-item"
                            style={{ gridColumn: "1 / -1" }}
                        >
                            <Select
                                size="large"
                                placeholder="Search user by name, email or phone…"
                                className="uqp-form-input"
                                showSearch
                                filterOption={false}
                                onSearch={handleUserSearch}
                                onPopupScroll={handlePopupScroll}
                                loading={usersLoading}
                                notFoundContent={usersLoading ? <Spin size="small" /> : "No users found"}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        {usersLoading && userOptions.length > 0 && (
                                            <div style={{ textAlign: "center", padding: "8px 0" }}>
                                                <Spin size="small" />
                                            </div>
                                        )}
                                    </>
                                )}
                            >
                                {userOptions.map((u) => (
                                    <Option key={u._id} value={u._id}>
                                        {u.name || u.user_id}
                                        {u.email ? ` (${u.email})` : ""}
                                        {u.contact_number ? ` — ${u.country_code || ""}${u.contact_number}` : ""}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Project Name"
                            name="project_name"
                            rules={[{ required: true, message: "Please enter the project name" }]}
                            className="uqp-form-item"
                        >
                            <Input size="large" placeholder="e.g. Fusion Homes" className="uqp-form-input" />
                        </Form.Item>

                        <Form.Item
                            label="Builder Name"
                            name="builder_name"
                            rules={[{ required: true, message: "Please enter the builder name" }]}
                            className="uqp-form-item"
                        >
                            <Input size="large" placeholder="e.g. ABC Builders" className="uqp-form-input" />
                        </Form.Item>

                        <Form.Item
                            label="Street Address"
                            name="street_address"
                            rules={[{ required: true, message: "Please enter the street address" }]}
                            className="uqp-form-item"
                            style={{ gridColumn: "1 / -1" }}
                        >
                            <Input.TextArea
                                rows={3}
                                placeholder="e.g. Sector 142, Noida"
                                className="uqp-form-input"
                            />
                        </Form.Item>
                    </div>
                </div>

                <Divider className="uqp-form-divider" />

                <Form.Item style={{ marginBottom: 0 }}>
                    <div className="uqp-form-actions">
                        <Button size="large" onClick={onClose} className="uqp-btn uqp-btn-cancel">
                            Cancel
                        </Button>
                        <Button
                            htmlType="submit"
                            size="large"
                            icon={<SaveOutlined />}
                            loading={submitting}
                            className="uqp-btn uqp-btn-primary"
                        >
                            <span style={{ color: "white" }}>
                                {recordData?._id ? "Update Entry" : "Add Entry"}
                            </span>
                        </Button>
                    </div>
                </Form.Item>

            </Form>
        </Modal>
    );
};

export default UserQueryProjectEditModal;